import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { DeplacementDossier } from './DeplacementDossier';
import { IdentifiantCopiable } from '../../components/IdentifiantCopiable';
import { Alert } from '../../components/Alert';
import { AuthCard } from '../../components/AuthCard';
import { ApiError } from '../../lib/api';
import {
  dossiers,
  libelleValeur,
  NOMS_PRODUITS,
  type Changement,
  type DroitMembre,
  type Formulaire,
  type Produit,
  type QuestionFormulaire,
  type ResultatSimulation,
} from '../../lib/dossiers';
import { ChampQuestion } from './ChampQuestion';
import { ChampsAdresse } from './ChampsAdresse';
import { ChampActivite, ChampFormeJuridique, ChampPays, ChampsCommune } from './ChampsReferentiels';
import { erreursParChamp, referentiels, type ActiviteRef } from '../../lib/referentiels';
import { useSessionPortail } from './useSessionPortail';
import '../../styles/dossiers.css';

interface Modif {
  valeur: unknown;
  effetLe: string;
  portee: Produit | '';
}

const aujourdhui = () => new Date().toISOString().slice(0, 10);

const CHAMPS_IDENTITE: { cle: string; libelle: string }[] = [
  { cle: 'name', libelle: 'Dénomination' },
  { cle: 'siret', libelle: 'SIRET' },
  // Code APE, forme juridique, pays, code postal et ville : par recherche dans
  // les référentiels (14/10/2026) — cf. <ChampsReferentiels />.
  { cle: 'apeCode', libelle: 'Code APE' },
  { cle: 'legalForm', libelle: 'Forme juridique' },
  { cle: 'vatNumber', libelle: 'N° de TVA intracommunautaire' },
  { cle: 'email', libelle: 'E-mail du dossier' },
  { cle: 'country', libelle: 'Pays' },
  { cle: 'postCode', libelle: 'Code postal et ville' },
];

const PAR_RECHERCHE = ['apeCode', 'legalForm', 'country', 'postCode'];

/**
 * Le formulaire global d'un dossier (11/10/2026).
 *
 * Mêmes questions qu'à la création. Avant d'enregistrer, chaque produit
 * concerné SIMULE le changement ; l'écran de confirmation liste les produits,
 * cochés par défaut — décocher un produit lui fait garder sa valeur actuelle
 * (valeur propre), un produit qui refuse est grisé avec son motif.
 */
export function FormulaireDossierPage() {
  const { companyId = '' } = useParams();
  const { pret } = useSessionPortail();
  const [formulaire, setFormulaire] = useState<Formulaire | null>(null);
  const [modifs, setModifs] = useState<Record<string, Modif>>({});
  const [simulation, setSimulation] = useState<ResultatSimulation[] | null>(null);
  const [coches, setCoches] = useState<Produit[]>([]);
  const [droits, setDroits] = useState<DroitMembre[] | null>(null);
  const [message, setMessage] = useState<{ texte: string; ok: boolean } | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [identite, setIdentite] = useState<Record<string, string>>({});
  const [erreursIdentite, setErreursIdentite] = useState<Record<string, string>>({});
  // Codes qui succèdent au code APE du dossier (table de correspondance, 14/10/2026).
  const [successeurs, setSuccesseurs] = useState<ActiviteRef[]>([]);
  const apeCode = formulaire?.dossier.apeCode ?? null;
  const apeNomenclature = formulaire?.dossier.apeNomenclature ?? null;
  const apeCodeSuivant = formulaire?.dossier.apeCodeSuivant ?? null;
  useEffect(() => {
    if (!apeCode || !apeNomenclature || apeCodeSuivant) return undefined;
    let actif = true;
    referentiels
      .successeurs(apeNomenclature, apeCode)
      .then((l) => {
        if (actif) setSuccesseurs(l);
      })
      .catch(() => undefined);
    return () => {
      actif = false;
    };
  }, [apeCode, apeNomenclature, apeCodeSuivant]);

  const charger = useCallback(async () => {
    try {
      setFormulaire(await dossiers.formulaire(companyId));
      setDroits(await dossiers.droits(companyId).catch(() => null));
    } catch (e) {
      setMessage({ texte: e instanceof ApiError ? e.message : 'Dossier introuvable.', ok: false });
    }
  }, [companyId]);

  useEffect(() => {
    if (pret && companyId) void charger();
  }, [pret, companyId, charger]);

  // Ancre `#cle` depuis le lien « Modifier » d'un produit.
  useEffect(() => {
    if (!formulaire) return;
    const ancre = window.location.hash.replace(/^#/, '');
    if (ancre) document.getElementById(`q-${ancre}`)?.scrollIntoView?.({ block: 'center' });
  }, [formulaire]);

  const changements: Changement[] = useMemo(
    () =>
      Object.entries(modifs).map(([cle, m]) => ({
        cle,
        valeur: m.valeur,
        effetLe: m.effetLe || null,
        portee: m.portee || null,
      })),
    [modifs],
  );

  function modifier(q: QuestionFormulaire, valeur: unknown) {
    setSimulation(null);
    setModifs((m) => ({
      ...m,
      [q.cle]: { valeur, effetLe: m[q.cle]?.effetLe ?? (q.datee ? aujourdhui() : ''), portee: m[q.cle]?.portee ?? '' },
    }));
  }

  async function verifier() {
    setEnvoi(true);
    setMessage(null);
    try {
      const r = await dossiers.simuler(companyId, changements);
      setSimulation(r.produits);
      // Un produit injoignable reste coché : il recevra le changement à son retour.
      setCoches(r.produits.filter((p) => p.resultat !== 'REFUSE').map((p) => p.produit));
    } catch (e) {
      setMessage({ texte: e instanceof ApiError ? e.message : 'Vérification impossible.', ok: false });
    } finally {
      setEnvoi(false);
    }
  }

  async function enregistrer() {
    setEnvoi(true);
    try {
      const r = await dossiers.appliquer(companyId, changements, coches);
      const enAttente = r.produits.filter((p) => p.coche && p.resultat === 'EN_ATTENTE');
      const refus = r.produits.filter((p) => p.coche && p.resultat === 'REFUSE');
      const noms = (l: typeof r.produits) => l.map((e) => NOMS_PRODUITS[e.produit]).join(', ');
      const parties = ['Modifications enregistrées.'];
      if (enAttente.length > 0) parties.push(`${noms(enAttente)} les recevra dès qu’il sera de nouveau joignable.`);
      if (refus.length > 0) {
        parties.push(
          `${noms(refus)} a refusé (${refus.map((e) => e.motif).filter(Boolean).join(' ; ')}) et garde l’ancienne valeur.`,
        );
      }
      setMessage({ texte: parties.join(' '), ok: refus.length === 0 });
      setModifs({});
      setSimulation(null);
      await charger();
    } catch (e) {
      setMessage({ texte: e instanceof ApiError ? e.message : 'Enregistrement impossible.', ok: false });
    } finally {
      setEnvoi(false);
    }
  }

  async function enregistrerIdentite() {
    setEnvoi(true);
    try {
      await dossiers.identite(companyId, identite);
      setIdentite({});
      setErreursIdentite({});
      setMessage({ texte: 'Identité du dossier enregistrée.', ok: true });
      await charger();
    } catch (e) {
      setMessage({ texte: e instanceof ApiError ? e.message : 'Enregistrement impossible.', ok: false });
      // Valeur absente des référentiels (14/10/2026) : l'erreur sous le champ.
      if (e instanceof ApiError) setErreursIdentite(erreursParChamp(e.details));
    } finally {
      setEnvoi(false);
    }
  }

  function annuler() {
    setModifs({});
    setSimulation(null);
    setMessage(null);
  }

  async function revenirCommune(cle: string, produit: Produit) {
    try {
      const r = await dossiers.retirerValeurPropre(companyId, cle, produit);
      if (r.synchro?.resultat === 'EN_ATTENTE') {
        setMessage({ texte: `${NOMS_PRODUITS[produit]} recevra la valeur commune dès qu’il sera joignable.`, ok: true });
      } else if (r.synchro?.resultat === 'DIVERGENCE') {
        setMessage({ texte: `${NOMS_PRODUITS[produit]} a refusé la valeur commune (${r.synchro.motif ?? ''}) et garde la sienne.`, ok: false });
      }
      await charger();
    } catch (e) {
      setMessage({ texte: e instanceof ApiError ? e.message : 'Action impossible.', ok: false });
    }
  }

  async function synchroniser(produit: Produit) {
    setEnvoi(true);
    try {
      const r = await dossiers.synchroniser(companyId, produit);
      setMessage(
        r.resultat === 'EN_ATTENTE'
          ? { texte: `${NOMS_PRODUITS[produit]} est toujours injoignable : nouvel essai automatique dans 30 minutes au plus.`, ok: false }
          : r.resultat === 'DIVERGENCE'
            ? { texte: `${NOMS_PRODUITS[produit]} a refusé (${r.motif ?? ''}) et garde l’ancienne valeur.`, ok: false }
            : { texte: `${NOMS_PRODUITS[produit]} est à jour.`, ok: true },
      );
      await charger();
    } catch (e) {
      setMessage({ texte: e instanceof ApiError ? e.message : 'Synchronisation impossible.', ok: false });
    } finally {
      setEnvoi(false);
    }
  }

  async function acquitter(produit: Produit) {
    try {
      await dossiers.acquitter(companyId, produit);
      await charger();
    } catch (e) {
      setMessage({ texte: e instanceof ApiError ? e.message : 'Action impossible.', ok: false });
    }
  }

  async function basculerDroit(m: DroitMembre) {
    try {
      setDroits(m.droitAccorde ? await dossiers.retirer(companyId, m.userId) : await dossiers.accorder(companyId, m.userId));
    } catch (e) {
      setMessage({ texte: e instanceof ApiError ? e.message : 'Action impossible.', ok: false });
    }
  }

  if (!pret || !formulaire) {
    return (
      <AuthCard title="Chargement…">
        <Alert message={message?.texte ?? null} />
      </AuthCard>
    );
  }

  const lectureSeule = !formulaire.peutModifier;

  return (
    <AuthCard
      className="dos-carte"
      title={formulaire.dossier.name}
      subtitle={`Informations du dossier · ${formulaire.produitsOuverts.map((p) => NOMS_PRODUITS[p]).join(' · ') || 'aucune application ouverte'}`}
    >
      <IdentifiantCopiable libelle="Identifiant du dossier" valeur={formulaire.dossier.id} />
      {formulaire.dossier.spaceId ? (
        <IdentifiantCopiable libelle="Identifiant de son espace" valeur={formulaire.dossier.spaceId} />
      ) : null}
      <Alert message={message?.texte ?? null} variant={message?.ok ? 'success' : 'error'} />
      {lectureSeule ? <p className="dos-aide">Vous pouvez consulter ces informations ; leur modification est réservée.</p> : null}

      {formulaire.synchros.map((s) => {
        const libelles = s.cles.map((cle) => formulaire.questions.find((q) => q.cle === cle)?.libelle ?? cle);
        return s.statut === 'EN_ATTENTE' ? (
          <div key={`${s.product}-${s.statut}`} className="dos-synchro" role="status">
            <p>
              <strong>{NOMS_PRODUITS[s.product]}</strong> n’a pas encore reçu {libelles.length} modification(s) :{' '}
              {libelles.join(', ')}. Nouvel essai automatique toutes les 30 minutes.
              {s.derniereErreur ? <small> Dernière erreur : {s.derniereErreur}</small> : null}
            </p>
            {!lectureSeule ? (
              <button type="button" className="btn btn-secondaire" disabled={envoi} onClick={() => void synchroniser(s.product)}>
                Synchroniser maintenant
              </button>
            ) : null}
          </div>
        ) : (
          <div key={`${s.product}-${s.statut}`} className="dos-synchro dos-synchro--refus" role="alert">
            <p>
              <strong>{NOMS_PRODUITS[s.product]}</strong> a refusé une modification ({s.motif ?? 'motif non précisé'}) et garde
              l’ancienne valeur pour : {libelles.join(', ')}. Elle apparaît comme valeur propre ; retirez-la quand le produit
              pourra suivre.
            </p>
            {!lectureSeule ? (
              <button type="button" className="btn btn-secondaire" onClick={() => void acquitter(s.product)}>
                Compris
              </button>
            ) : null}
          </div>
        );
      })}

      <details className="dos-repli">
        <summary>Identité du dossier</summary>
        {CHAMPS_IDENTITE.map((c) => (
          <Fragment key={c.cle}>
            {/* Adresse en zones (13/10/2026), avant le code postal. */}
            {c.cle === 'postCode' ? (
              <ChampsAdresse
                prefixe="idf"
                erreurs={erreursIdentite}
                disabled={lectureSeule || envoi}
                valeur={(cle) => identite[cle] ?? String(formulaire.dossier[cle] ?? '')}
                onChange={(cle, v) => setIdentite((i) => ({ ...i, [cle]: v }))}
              />
            ) : null}
            {PAR_RECHERCHE.includes(c.cle) ? (
              (() => {
                const props = {
                  prefixe: 'idf',
                  erreurs: erreursIdentite,
                  disabled: lectureSeule || envoi,
                  valeur: (cle: string) => identite[cle] ?? String(formulaire.dossier[cle] ?? ''),
                  onChange: (champs: Record<string, string>) => setIdentite((i) => ({ ...i, ...champs })),
                };
                if (c.cle === 'apeCode') {
                  // Code APE à venir (relevé dans SIRENE) ou, à défaut, successeurs possibles.
                  const d = formulaire.dossier;
                  const complement = d.apeCodeSuivant ? (
                    <>
                      {' '}
                      Code {d.apeNomenclatureSuivante === 'NAF_2025' ? 'NAF 2025' : (d.apeNomenclatureSuivante ?? '')} à venir
                      (SIRENE) : <strong>{d.apeCodeSuivant}</strong>
                      {d.apeLabelSuivant ? ` — ${d.apeLabelSuivant}` : ''}.
                    </>
                  ) : successeurs.length > 0 ? (
                    <>
                      {' '}
                      {successeurs.length > 1 ? 'Codes' : 'Code'} {successeurs[0]?.nomenclatureLibelle ?? ''} possible(s) :{' '}
                      {successeurs.map((x) => `${x.code} (${x.libelle})`).join(', ')}.
                    </>
                  ) : null;
                  return <ChampActivite {...props} complement={complement} />;
                }
                if (c.cle === 'legalForm') return <ChampFormeJuridique {...props} />;
                if (c.cle === 'country') return <ChampPays {...props} />;
                return <ChampsCommune {...props} />;
              })()
            ) : (
              <div className={`field${erreursIdentite[c.cle] ? ' has-erreur' : ''}`}>
                <label htmlFor={`idf-${c.cle}`}>{c.libelle}</label>
                <input
                  id={`idf-${c.cle}`}
                  value={identite[c.cle] ?? formulaire.dossier[c.cle] ?? ''}
                  disabled={lectureSeule || envoi}
                  onChange={(ev) => setIdentite((i) => ({ ...i, [c.cle]: ev.target.value }))}
                />
                {erreursIdentite[c.cle] ? <p className="dos-erreur">{erreursIdentite[c.cle]}</p> : null}
              </div>
            )}
          </Fragment>
        ))}
        {Object.keys(identite).length > 0 ? (
          <button type="button" className="btn" onClick={() => void enregistrerIdentite()} disabled={envoi}>
            Enregistrer l’identité
          </button>
        ) : null}
      </details>

      {formulaire.etapes.map((e) => (
        <section key={e.id}>
          <h2 className="dos-section">{e.libelle}</h2>
          {formulaire.questions
            .filter((q) => q.etape === e.id)
            .map((q) => {
              const m = modifs[q.cle];
              return (
                <div key={q.cle} className={m ? 'dos-modifie' : undefined}>
                  <ChampQuestion
                    question={{ ...q, libelle: q.libelle }}
                    valeur={m ? m.valeur : q.valeur}
                    disabled={lectureSeule || envoi}
                    onChange={(v) => modifier(q, v)}
                  />
                  <div className="dos-sous-champs">
                    <span>Utilisé par : {q.correspondances.map((p) => NOMS_PRODUITS[p]).join(', ')}</span>
                    {Object.entries(q.propres).map(([p, v]) => (
                      <span key={p} className="dos-badge dos-badge--propre">
                        Valeur propre à {NOMS_PRODUITS[p as Produit]} : {libelleValeur(q, v)}
                        {!lectureSeule ? (
                          <button type="button" className="adm-link" onClick={() => void revenirCommune(q.cle, p as Produit)}>
                            {' '}
                            (revenir à la valeur commune)
                          </button>
                        ) : null}
                      </span>
                    ))}
                  </div>
                  {m && q.datee ? (
                    <div className="dos-sous-champs">
                      <label>
                        À partir du
                        <input
                          type="date"
                          value={m.effetLe}
                          onChange={(ev) => setModifs((x) => ({ ...x, [q.cle]: { ...m, effetLe: ev.target.value } }))}
                        />
                      </label>
                    </div>
                  ) : null}
                  {m && q.surchargeAutorisee && q.correspondances.length > 1 ? (
                    <div className="dos-sous-champs">
                      <label>
                        Appliquer à
                        <select
                          value={m.portee}
                          onChange={(ev) =>
                            setModifs((x) => ({ ...x, [q.cle]: { ...m, portee: ev.target.value as Produit | '' } }))
                          }
                        >
                          <option value="">Tous les produits concernés</option>
                          {q.correspondances.map((p) => (
                            <option key={p} value={p}>
                              Seulement {NOMS_PRODUITS[p]}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                  ) : null}
                </div>
              );
            })}
        </section>
      ))}

      {changements.length > 0 && !simulation ? (
        <div className="dos-actions">
          <button type="button" className="btn btn-secondaire" onClick={annuler} disabled={envoi}>
            Annuler
          </button>
          <button type="button" className="btn" onClick={() => void verifier()} disabled={envoi}>
            {envoi ? 'Vérification…' : 'Vérifier les effets'}
          </button>
        </div>
      ) : null}

      {simulation ? (
        <div className="dos-confirmation">
          <strong>Effet de vos modifications</strong>
          {simulation.length === 0 ? <p>Aucune application n’est concernée : seules vos réponses seront mises à jour.</p> : null}
          {simulation.map((s) => (
            <label key={s.produit} className="dos-ligne-produit">
              <input
                type="checkbox"
                checked={coches.includes(s.produit)}
                disabled={s.resultat === 'REFUSE' || envoi}
                onChange={() =>
                  setCoches((c) => (c.includes(s.produit) ? c.filter((x) => x !== s.produit) : [...c, s.produit]))
                }
              />
              <span>
                <strong>{NOMS_PRODUITS[s.produit]}</strong>
                <small>
                  {s.resultat === 'ACCEPTE'
                    ? s.effet ?? 'Modification acceptée.'
                    : s.resultat === 'REFUSE'
                      ? `Refusé : ${s.motif ?? ''}${s.datePossible ? ` — possible à partir du ${libelleValeur({ type: 'DATE' }, s.datePossible)}` : ''}`
                      : `Injoignable pour l’instant : recevra la modification dès son retour (essai automatique toutes les 30 minutes).`}
                </small>
                {s.resultat !== 'REFUSE' && !coches.includes(s.produit) ? (
                  <small>{NOMS_PRODUITS[s.produit]} gardera sa valeur actuelle.</small>
                ) : null}
              </span>
            </label>
          ))}
          <div className="dos-actions">
            <button type="button" className="btn btn-secondaire" onClick={annuler} disabled={envoi}>
              Annuler
            </button>
            <button type="button" className="btn" onClick={() => void enregistrer()} disabled={envoi}>
              {envoi ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </div>
      ) : null}

      {droits ? (
        <section>
          <h2 className="dos-section">Qui peut modifier ces informations</h2>
          <ul className="dos-recap">
            {droits.map((m) => (
              <li key={m.userId}>
                <span>
                  {m.nom ?? m.email}
                  {m.role !== 'MEMBER' ? <span className="dos-badge">{m.role === 'OWNER' ? 'Propriétaire' : 'Administrateur'}</span> : null}
                  {m.profil === 'LECTURE_SEULE' ? <span className="dos-badge">Lecture seule</span> : null}
                </span>
                <span>
                  {m.modifiable ? (
                    <label>
                      <input type="checkbox" checked={m.droitAccorde} onChange={() => void basculerDroit(m)} /> Peut modifier
                    </label>
                  ) : m.peutModifier ? (
                    'Toujours'
                  ) : (
                    'Jamais'
                  )}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <DeplacementDossier companyId={formulaire.dossier.id} />

      <div className="links">
        <Link to="/espace">Mon espace</Link>
        <Link to={`/dossiers/nouveau?dossier=${formulaire.dossier.id}`}>Ouvrir une autre application</Link>
      </div>
    </AuthCard>
  );
}
